import * as DialogPrimitive from "@radix-ui/react-dialog";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The app's destructive-action confirmation.
 *
 * Built on Radix's Dialog rather than a bare fixed-position div, because most
 * of the places that ask for confirmation are *inside* a ResponsiveSheet — and
 * on mobile that's a vaul drawer, i.e. a Radix dialog, which sets
 * `pointer-events: none` on <body> for as long as it's open. A plain div
 * rendered next to it inherits that and goes completely dead: on the mobile map
 * you could open a thought, tap the bin, and then neither Delete, Cancel, nor
 * the backdrop would respond — the card was undeletable and the dialog was
 * unescapable. Registering as a real layer fixes both halves: this dialog
 * becomes the topmost layer (so it gets pointer events back) and the drawer
 * underneath stops treating taps in here as "outside" clicks that dismiss it.
 *
 * Escape and a backdrop tap both cancel, as before.
 */
export function ConfirmDialog({
  open,
  title,
  description = "This can't be undone.",
  confirmLabel = "Delete",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-foreground/10 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          // Entry animation only, deliberately. Radix keeps an exiting node
          // mounted until `animationend`, and a frozen animation — a
          // backgrounded tab, a device that skips it — would leave this layer
          // up and the sheet underneath inert: the exact stuck state this
          // component exists to avoid. No exit animation, no dependency on it.
          //
          // w-[calc(100%-2rem)]: keeps a gutter on narrow phones, where
          // `w-full max-w-sm` left the card all but touching both edges.
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[71] w-[calc(100%-2rem)] max-w-sm bg-card rounded-2xl border border-border/40 shadow-2xl p-6 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Title
            className="text-primary mb-2"
            style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.1rem", fontWeight: 400 }}
          >
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-muted-foreground mb-6" style={{ fontSize: "0.85rem" }}>
            {description}
          </DialogPrimitive.Description>
          <div className="flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-all"
              style={{ fontSize: "0.85rem" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="px-4 py-2 rounded-xl bg-destructive text-destructive-foreground hover:opacity-90 transition-all"
              style={{ fontSize: "0.85rem" }}
            >
              {confirmLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
